import { APP_ROUTES } from '../../../constants';
import { useNavigate } from 'react-router-dom';
import { useQuery } from 'react-query';
import { isAxiosError } from 'axios';

import { createHSAConfig, getHsaConfigLists } from 'api';

import { useAtom, useSetAtom } from 'jotai';
import { setISRecalculatingHSAAtom } from 'atoms/isRecalculatingHSA';
import { useOnboardHSAConfigAtom, useOnboardingStepsAtom } from 'atoms';
import { selectedCustomerAtom } from 'atoms/useSelectedCustomerAtom';

import { TransformedData } from 'types/editPage';
import { HSAData, HSAformData, POSTErrorRes } from 'types';
import { toast } from 'react-toastify';
import { formatDateTime } from 'utils';

// Extracts the backend-provided error message (if present) instead of always
// falling back to a generic message, so users see the actual failure reason.
const getErrorMessage = (error: unknown, fallback: string): string => {
  if (isAxiosError<POSTErrorRes>(error)) {
    return error.response?.data?.error?.message || fallback;
  }
  return fallback;
};

interface SavingsAssessment {
  customer: TransformedData;
  enabled: boolean;
}

// This component is responsible to initiate the Recalculating of HSA for customers -
// who either have a Draft HSA or have one or more Completed or Failed HSA (Customers with AutoOptmize module enabled will not see this comp)
function SavingsAssessment({ customer, enabled }: SavingsAssessment) {
  const [, setHSAConfig] = useOnboardHSAConfigAtom();
  const setIsRecalculatingHSA = useSetAtom(setISRecalculatingHSAAtom);
  const { setCurrentStep } = useOnboardingStepsAtom();
  const navigate = useNavigate();
  const [, setSelectedCustomer] = useAtom(selectedCustomerAtom);

  const { data: hsaConfigData } = useQuery({
    queryKey: ['hsaConfigData', customer?.customer_id],
    queryFn: () => getHsaConfigLists(customer?.customer_id ?? ''),
    enabled: !!customer?.customer_id,
  });
  const hsaStatus = hsaConfigData?.data[0].status ?? 'No';
  const hsaStatusDraft = hsaStatus === 'Draft';
  const hsaStatusProcessing = hsaStatus === 'Processing';
  const hsaStatusFailed = hsaStatus === 'Failed';

  // -------    Handle Create new Assessment  -----
  // Create new hsaConfig
  const handleCreateNewHSA = async () => {
    if (!customer?.customer_id || !customer?.agency || !customer?.client) {
      console.warn(
        'handleCreateNewHSA called without customer_id or customer does not have agency or client'
      );
      return;
    }

    try {
      const { data: newHSAConfig } = await createHSAConfig(
        customer.customer_id
      );

      // Update the hsaConfig atom useOnboardHSAConfigAtom
      setHSAConfig(newHSAConfig);

      // Update selectedCustomer atom - this populates form data for HSA onboarding for all steps (all forms)
      const hsaFormData: HSAformData = {
        customer_id: customer.customer_id ?? '',
        agency: customer.agency ?? '',
        client: customer.client ?? '',
        line_of_business: customer.line_of_business ?? '',
        market: customer.market ?? '',
        region: customer.region ?? '',
        status: customer.status ?? '',
        new_business: customer.new_business ?? false,
        platform: customer.platform?.length ? customer.platform : [''],
        platforms: Array.isArray(customer.platforms)
          ? customer.platforms
          : [false, false, false, false],
        account_configurations: customer.account_configurations ?? [],
        googleEmail: customer.google_email_account,
        hsa_id: newHSAConfig?.hsa_id,
        hsa_status: newHSAConfig?.status,
        brand_signifiers: newHSAConfig?.brand_signifiers,
        whitelisted_domains: newHSAConfig?.whitelisted_domains,
        created_at: newHSAConfig?.created_at,
        updated_at: newHSAConfig?.updated_at,
      };

      // Update atoms before navigating to Onboarding form
      setSelectedCustomer(hsaFormData); // This atom populates the HSA form fields for all the HSA steps
      setIsRecalculatingHSA(true);
      setCurrentStep(2); // Properties is step 2 — required for back-button guard in Onboarding.tsx

      // Let React flush updates before navigation with setTimeout
      setTimeout(() => {
        navigate(APP_ROUTES.ONBOARD_HSA_PROPERTIES);
      }, 0);
    } catch (error) {
      console.error('Failed to create new HSA:', error);
      toast.error(
        getErrorMessage(error, 'Something went wrong while creating HSA')
      );
    }
  };

  // ------   Handle the Draft HSA recalculate flow   ------
  const handleDraftRecalculateHSA = async () => {
    if (!customer?.customer_id) {
      console.warn('handleDraftRecalculateHSA called without customer_id');
      return;
    }

    try {
      // Fetch previous hsaConfig data
      const response = await getHsaConfigLists(customer.customer_id);
      const hsaConfig = response?.data?.[0];

      if (!hsaConfig) {
        console.warn('No HSA config found for customer', customer.customer_id);
        return;
      }

      // Normalize the HSA config
      const normalizedHsaConfig: HSAData = {
        customer_id: Number(customer.customer_id),
        hsa_id: hsaConfig.hsa_id,
        status: hsaConfig.status,
        brand_signifiers: hsaConfig.brand_signifiers,
        whitelisted_domains: hsaConfig.whitelisted_domains ?? [],
        prelim_start_date: hsaConfig.prelim_start_date ?? null,
        prelim_end_date: hsaConfig.prelim_end_date ?? null,
        created_at: hsaConfig.created_at ?? '',
        updated_at: hsaConfig.updated_at ?? '',
        updated_by: Number(hsaConfig.updated_by),
      };

      // Update hsaConfig atom
      setHSAConfig(normalizedHsaConfig);

      // Prepare HSA form data (to set selectedCustomer atom)
      const hsaFormData: HSAformData = {
        customer_id: customer.customer_id ?? '',
        agency: customer.agency ?? '',
        client: customer.client ?? '',
        line_of_business: customer.line_of_business ?? '',
        market: customer.market ?? '',
        region: customer.region ?? '',
        status: customer.status ?? '',
        new_business: customer.new_business ?? false,
        platform: customer.platform?.length ? customer.platform : [''],
        platforms: Array.isArray(customer.platforms)
          ? customer.platforms
          : [false, false, false, false],
        account_configurations: customer.account_configurations ?? [],
        googleEmail: customer.google_email_account,
        hsa_id: hsaConfig.hsa_id,
        hsa_status: hsaConfig.status ?? 'No',
        brand_signifiers: hsaConfig.brand_signifiers,
        whitelisted_domains: hsaConfig.whitelisted_domains ?? [],
        created_at: hsaConfig?.created_at ?? '',
        updated_at: hsaConfig.updated_at ?? '',
      };

      // Update atoms
      setSelectedCustomer(hsaFormData); // This atom populates the HSA form fields for all the HSA steps
      setIsRecalculatingHSA(true);
      setCurrentStep(2); // Properties is step 2 — required for back-button guard in Onboarding.tsx

      // Let React flush updates before navigation
      setTimeout(() => {
        navigate(APP_ROUTES.ONBOARD_HSA_PROPERTIES);
      }, 0);
    } catch (error) {
      console.error('Error recalculating HSA draft:', error);
      toast.error(
        getErrorMessage(
          error,
          'Something went wrong while recalculating HSA draft. Please try again.'
        )
      );
    }
  };

  // Date time formatted to local format
  const lastUpdatedOn = formatDateTime(hsaConfigData?.data[0]?.updated_at);

  return enabled ? (
    <>
      <div>
        <h1 className="text-2xl font-bold mb-4">Savings Assessment</h1>
      </div>

      <div className="mt-9">
        {/* Create new Assessment */}
        <div className="flex items-center gap-2 mb-4">
          <button
            className="px-6 py-3 bg-blue-600 text-white font-semibold rounded-full hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-blue-800"
            onClick={() => handleCreateNewHSA()}
            disabled={hsaStatusDraft || hsaStatusProcessing}
          >
            Create new Assessment
          </button>
        </div>

        {/* Your Draft */}
        <div className="flex items-center gap-10 p-3 rounded-lg mt-3 border-b border-gray-200">
          {
            hsaStatusProcessing &&
            <div>
              <span className="block font-medium">Processing HSA</span>
              <p className="text-sm text-gray-600">
                {lastUpdatedOn
                  ? `Last update on: ${lastUpdatedOn}`
                  : 'No saved draft'}
              </p>
            </div>
          }

          {
            hsaStatusFailed &&
            <div>
              <span className="block font-medium text-red-700">
                HSA Assessment Failed
              </span>
              <p className="text-sm text-gray-600">
                {lastUpdatedOn
                  ? `Last attempt on: ${lastUpdatedOn}. Please create a new Assessment to retry.`
                  : 'Please create a new Assessment to retry.'}
              </p>
            </div>
          }

          {
            !hsaStatusProcessing && !hsaStatusFailed &&
            <>
              <div>
                <span className="block font-medium">Your Draft</span>
                <p className="text-sm text-gray-600">
                  {hsaStatusDraft
                    ? `Last update on: ${lastUpdatedOn}`
                    : 'No saved draft'}
                </p>
              </div>
              <button
                className="px-4 py-2 bg-blue-800 text-white rounded-full
                hover:bg-green-700
                disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-blue-800"
                onClick={() => handleDraftRecalculateHSA()}
                disabled={!hsaStatusDraft}
              >
                Continue
              </button>
            </>
          }


        </div>
      </div>
    </>
  ) : null;
}

export default SavingsAssessment;
